import { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../utils/axios.ts';
import { formatCurrency } from '../hooks/useLoanRepayments.ts';

export default function GuarantorRequestForm({ 
  loanData, 
  onSubmit, 
  onCancel, 
  onGuarantorSelection, 
  selectedGuarantors, 
  isLoading 
}) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Fetch members for guarantor selection
  useEffect(() => {
    const fetchMembers = async () => {
      setLoadingMembers(true);
      try {
        // Pass loan_id to exclude loan applicant and existing guarantors
        const response = await api.get(`/members/loan-application?loan_id=${loanData.id}`);
        const members = response.data.data || [];
        setMembers(members);
      } catch (error) {
        console.error('Failed to fetch members:', error);
        toast.error('Failed to fetch members for guarantor selection');
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [loanData.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedGuarantors.length === 0) {
      toast.error('Please select at least one guarantor');
      return;
    }
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Loan Information</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-blue-700">Principal Amount:</span>
            <p className="font-medium text-blue-900">{formatCurrency(loanData.principal)}</p>
          </div>
          <div>
            <span className="text-blue-700">Required Guarantors:</span>
            <p className="font-medium text-blue-900">{loanData.guarantor_amount ? formatCurrency(loanData.guarantor_amount) : 'Not specified'}</p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-gray-900 mb-4">Select Guarantors</h4>
        {loadingMembers ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading members...</span>
          </div>
        ) : members.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {members.map((member) => (
              <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`guarantor-${member.id}`}
                      checked={selectedGuarantors.includes(member.id)}
                      onChange={(e) => onGuarantorSelection(member.id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`guarantor-${member.id}`} className="ml-3 flex-1">
                      <div className="font-medium text-gray-900">{member.name}</div>
                      <div className="text-sm text-gray-500">{member.phone_number}</div>
                    </label>
                  </div>

                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Eligible Members Available</h3>
            <p className="text-gray-500 mb-4">
              All available members have either already been requested as guarantors or are not eligible.
            </p>
            <div className="text-sm text-gray-400 space-y-1">
              <p>• You cannot select yourself as a guarantor</p>
              <p>• Members who have already received guarantor requests are excluded</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || selectedGuarantors.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Sending Requests...' : 'Send Guarantor Requests'}
        </button>
      </div>
    </form>
  );
} 